import {
  AnalystRow,
  AnalystsResponse,
  ChatMessageRow,
  ChatMessagesResponse,
  MemoryResponse,
  MemoryRow,
  WorkflowRow,
  WorkflowsResponse,
} from "modules/interfaces";
import { dateToEpochMs, formatDateToYMD } from "utils/dateUtils";
import { getChatUsageUrl, getMemoryUsageUrl } from "./darwinProperties";
import { getAnalystUsageUrl, getScheduleJobUsageUrl } from "./newtonProperties";

export type {
  AnalystRow,
  AnalystsResponse,
  ChatMessageRow,
  ChatMessagesResponse,
  MemoryResponse,
  MemoryRow,
  WorkflowRow,
  WorkflowsResponse,
};

function triggerCsvDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

export async function fetchChatMessages(startDate: Date, endDate: Date, offset: number): Promise<ChatMessagesResponse> {
  const url = new URL(getChatUsageUrl());
  url.searchParams.set("start_ms", String(dateToEpochMs(startDate)));
  url.searchParams.set(
    "end_ms",
    String(dateToEpochMs(new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1)) - 1)
  );
  url.searchParams.set("offset", String(offset));

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail ?? "Failed to fetch usage metrics.");
  }

  return response.json();
}

export async function downloadChatMessagesCsv(startDate: Date, endDate: Date): Promise<void> {
  const url = new URL(getChatUsageUrl());
  url.searchParams.set("start_ms", String(dateToEpochMs(startDate)));
  url.searchParams.set(
    "end_ms",
    String(dateToEpochMs(new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1)) - 1)
  );
  url.searchParams.set("download_csv", "true");

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to download CSV.");
  }

  triggerCsvDownload(
    await response.blob(),
    `chat_messages_${formatDateToYMD(startDate)}_to_${formatDateToYMD(endDate)}.csv`
  );
}

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------

export async function fetchWorkflows(offset: number): Promise<WorkflowsResponse> {
  const url = new URL(getScheduleJobUsageUrl());
  url.searchParams.set("offset", String(offset));

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail ?? "Failed to fetch workflow usage metrics.");
  }

  return response.json();
}

export async function downloadWorkflowsCsv(): Promise<void> {
  const url = new URL(getScheduleJobUsageUrl());
  url.searchParams.set("downloadCsv", "true");

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to download workflows CSV.");
  }

  triggerCsvDownload(await response.blob(), "workflows.csv");
}

// ---------------------------------------------------------------------------
// Analysts
// ---------------------------------------------------------------------------

export async function fetchAnalysts(offset: number): Promise<AnalystsResponse> {
  const url = new URL(getAnalystUsageUrl());
  url.searchParams.set("offset", String(offset));

  const response = await fetch(url.toString(), { method: "GET", credentials: "include" });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail ?? "Failed to fetch analyst usage metrics.");
  }
  return response.json();
}

export async function downloadAnalystsCsv(): Promise<void> {
  const url = new URL(getAnalystUsageUrl());
  url.searchParams.set("downloadCsv", "true");

  const response = await fetch(url.toString(), { method: "GET", credentials: "include" });
  if (!response.ok) {
    throw new Error("Failed to download analysts CSV.");
  }

  triggerCsvDownload(await response.blob(), "analysts.csv");
}

// ---------------------------------------------------------------------------
// Memory
// ---------------------------------------------------------------------------

export async function fetchMemory(offset: number): Promise<MemoryResponse> {
  const url = new URL(getMemoryUsageUrl());
  url.searchParams.set("offset", String(offset));

  const response = await fetch(url.toString(), { method: "GET", credentials: "include" });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail ?? "Failed to fetch memory usage metrics.");
  }
  return response.json();
}

export async function downloadMemoryCsv(): Promise<void> {
  const url = new URL(getMemoryUsageUrl());
  url.searchParams.set("download_csv", "true");

  const response = await fetch(url.toString(), { method: "GET", credentials: "include" });
  if (!response.ok) {
    throw new Error("Failed to download memory CSV.");
  }

  triggerCsvDownload(await response.blob(), "memory.csv");
}
